import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import Card from '@deporvillage/card-component';
import Notification from '@deporvillage/notification-component';
import ReadMore from '@deporvillage/read-more-component';
import Reviews from '@deporvillage/reviews-component';
import TabContentWrapper from '@deporvillage/tab-content-wrapper-component';
import { track } from '@deporvillage/tracking-library-component';
import {
    Config,
    CookieCompliance,
    CookieComplianceCategories,
    I18n,
    I18nContext,
} from '@deporvillage/utils-library-component';

import styles from './Tabs.module.scss';

const TabType = {
    Description: 1,
    Reviews: 2,
    Shipping: 3,
    ReviewsRelated: 4,
};

const ProductPageTabs = ({
    product, clickTab, clickMoreReviews, clickMoreReviewsRelated, clickAddReview, activeTab,
}) => {
    const {
        description, sku, bikeMount, nonReturnable,
        expertOpinion, reviews, reviewsRelated,
    } = product;

    const [desc, setDesc] = React.useState(description.replace(/<iframe(?:.*?)>.*?<\/iframe>/g, ''));

    const { translate } = React.useContext(I18nContext);
    const { express_delivery: expressDelivery } = React.useContext(Config);

    const handleInit = (canRun) => {
        if (!canRun) return;
        setDesc(description);
    };

    React.useEffect(() => {
        CookieCompliance(handleInit, CookieComplianceCategories.Functional);
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, []);

    const renderTabDescription = () => (
        <div className={styles['tab-container']}>
            <div className={styles['tab-column-left']}>
                <ReadMore height={200} onClick={
                    /* istanbul ignore next */
                    () =>
                        track({
                            type: 'ACTION',
                            name: 'GENERIC_CLICK',
                            action: 'Ver descripción',
                            label: 'Click en ver más',
                        })} >
                    <Fragment>
                        <div dangerouslySetInnerHTML={{ __html: desc }} />
                        {
                            bikeMount ?
                                <div className={styles['bike-mount-notice']}>
                                    <I18n>product_page.title.bike_mount_description</I18n>
                                </div> : null
                        }
                        <div className={styles['sku-reference']}>
                            <I18n>product_page.title.reference</I18n> <span>{sku}</span>
                        </div>
                        <div className={styles['expert-opinion-bottom-wrapper']}>
                            {renderExpertOpinion()}
                        </div>
                    </Fragment>
                </ReadMore>
            </div>
            <div className={`${styles['tab-column-right']} ${styles['expert-opinion-left-wrapper']}`}>
                {renderExpertOpinion()}
            </div>
        </div>
    );

    const renderExpertOpinion = () => (
        <div onClick={ /* istanbul ignore next */ (e) => e.target.nodeName === 'A' && track({
            type: 'ACTION',
            name: 'GENERIC_CLICK',
            action: 'Ir a la review del blog',
            label: 'Click en ver review Blog',
        })}>
            <Card
                title={<I18n>product_page.title.expert_opinion</I18n>}
                content={<span className={styles['expert-opinion-quote']} dangerouslySetInnerHTML={{ __html: expertOpinion }} />}
            />
        </div>
    );

    const renderTabShippingAndRefunds = () => (
        <div className={styles['tab-container']}>
            <div className={styles['tab-column-left']}>
                {nonReturnable ?
                    <div className={styles['notification-wrapper']}>
                        <Notification message={<I18n>product_page.shipping.not_return</I18n>} type='light' />
                    </div> : null
                }

                <p>
                    <strong><I18n>product_page.shipping.disponibility_title</I18n></strong>&nbsp;
                    {expressDelivery ? (
                        <I18n>product_page.shipping.disponibility_shipping</I18n>
                    ) : (
                        <I18n>product_page.shipping.disponibility_delivery</I18n>
                    )}
                </p>

                <p>
                    <strong>
                        <I18n>product_page.shipping.payment_title</I18n>
                    </strong> <I18n>product_page.shipping.payment</I18n>
                </p>

                <p>
                    <strong><I18n>product_page.shipping.shipping_title</I18n></strong>&nbsp;
                    <span
                        dangerouslySetInnerHTML={{
                            __html: translate('product_page.shipping.shipping', {
                                shipping_url: translate('product_page.shipping.shipping_url'),
                            }),
                        }
                        } />
                </p>
                <p>
                    <strong><I18n>product_page.shipping.returns_title</I18n></strong>&nbsp;
                    <span
                        dangerouslySetInnerHTML={{
                            __html: translate('product_page.shipping.returns', {
                                returns_url: translate('product_page.shipping.returns_url'),
                            }),
                        }
                        } />
                </p>
                <p>
                    <strong><I18n>product_page.shipping.size_changes_title</I18n></strong>&nbsp;
                    <span
                        dangerouslySetInnerHTML={{
                            __html: translate('product_page.shipping.size_changes', {
                                returns_url: translate('product_page.shipping.returns_url'),
                            }),
                        }
                        } />

                </p>
            </div>
        </div>
    );

    const renderTabReviews = () => (
        <div className={`${styles['tab-container']} ${styles['tab-reviews']}`} id="tab-reviews">
            <div className={styles['tab-column-left']}>
                {
                    reviews?.data && reviews?.pagination?.totalCount ? (
                        <Reviews
                            items={reviews?.data}
                            count={reviews?.pagination?.totalCount}
                            click={clickMoreReviews}
                        />
                    ) : (
                        <Notification message={<I18n>product_page.reviews.reviews_first_review</I18n>} type={'info'} />
                    )
                }
            </div>
            <div className={styles['tab-column-right']} data-testid={'product-write-review-tab'}>
                <button onClick={clickAddReview} className={styles['write-review-button']}>
                    <i className={`${styles.icon} icon icon-comment`} />
                    <span><I18n>product_page.reviews.write_review</I18n></span>
                </button>
            </div>
        </div>
    );

    const renderTabRelatedReviews = () => (
        <div className={`${styles['tab-container']} ${styles['tab-reviews']}`} id="tab-reviews-related">
            <div className={styles['tab-column-left']}>
                {
                    <Reviews
                        items={reviewsRelated?.data}
                        count={reviewsRelated?.pagination?.totalCount}
                        click={clickMoreReviewsRelated} />
                }
            </div>
        </div>
    );

    return (
        <div className={styles['product-page-tabs-component']}>
            <TabContentWrapper click={clickTab} tab={activeTab} items={[
                {
                    id: TabType.Description,
                    title: <I18n>product_page.title.description</I18n>,
                    content: renderTabDescription(),
                },
                {
                    id: TabType.Reviews,
                    title: <Fragment>
                        <I18n>product_page.title.client_reviews</I18n>
                        {
                            reviews?.pagination?.totalCount ? ` (${reviews?.pagination?.totalCount})` : ''
                        }
                    </Fragment>,
                    content: renderTabReviews(),
                },
                reviewsRelated?.pagination?.totalCount ? {
                    id: TabType.ReviewsRelated,
                    title: <Fragment>
                        <I18n>product_page.title.client_reviews_related</I18n> {`(${reviewsRelated?.pagination?.totalCount})`}
                    </Fragment>,
                    content: renderTabRelatedReviews(),
                } : null,
                {
                    id: TabType.Shipping,
                    title: <I18n>product_page.title.shipping_and_returns</I18n>,
                    content: renderTabShippingAndRefunds(),
                },
            ]} />
        </div>
    );
};

ProductPageTabs.propTypes = {
    product: PropTypes.object,
    clickTab: PropTypes.func,
    clickAddReview: PropTypes.func,
    clickMoreReviews: PropTypes.func,
    clickMoreReviewsRelated: PropTypes.func,
    activeTab: PropTypes.oneOf([
        TabType.Description,
        TabType.Reviews,
        TabType.Shipping,
        TabType.ReviewsRelated,
    ]),
};

export default ProductPageTabs;
export { TabType as ProductTabType };
